import React, { useState } from 'react';
import { Trash2, ArrowUp, ArrowDown, Upload, Loader2, Building2, Edit2, Check, X, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useClientLogos } from '../../hooks/useClientLogos';
import { compressLogo } from '../../utils/imageUtils';
import { BulkImportLogos } from './BulkImportLogos';
export function ClientLogosSection() {
  const {
    logos,
    addLogo,
    deleteLogo,
    moveLogo,
    updateLogo,
    deleteAllLogos
  } = useClientLogos();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Form states
  const [newLogoName, setNewLogoName] = useState('');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [editLogoName, setEditLogoName] = useState('');
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsProcessing(true);
      try {
        const file = e.target.files[0];
        // Usar compressLogo en lugar de compressImage para preservar transparencia
        const compressed = await compressLogo(file);
        setNewLogoUrl(compressed);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Error al procesar la imagen.');
      } finally {
        setIsProcessing(false);
        e.target.value = '';
      }
    }
  };
  const handleAddLogo = async () => {
    if (!newLogoName.trim() || !newLogoUrl) {
      alert('Por favor completa el nombre y sube una imagen.');
      return;
    }
    await addLogo({
      name: newLogoName.trim(),
      url: newLogoUrl
    });
    // Reset form
    setNewLogoName('');
    setNewLogoUrl('');
    setIsAdding(false);
  };
  const handleBulkImport = async (importedLogos: Array<{
    name: string;
    url: string;
  }>) => {
    for (const logo of importedLogos) {
      await addLogo(logo);
    }
  };
  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar este logo?')) {
      deleteLogo(id);
    }
  };
  const handleDeleteAll = () => {
    if (window.confirm(`¿Estás seguro de eliminar TODOS los ${logos.length} logos? Esta acción no se puede deshacer.`)) {
      if (window.confirm('⚠️ CONFIRMACIÓN FINAL: Se eliminarán todos los logos permanentemente.')) {
        deleteAllLogos();
      }
    }
  };
  const startEdit = (logo: any) => {
    setEditingId(logo.id);
    setEditLogoName(logo.name);
  };
  const saveEdit = async (id: string) => {
    await updateLogo(id, {
      name: editLogoName.trim()
    });
    setEditingId(null);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditLogoName('');
  };
  return <>
      {/* Bulk Import Section */}
      <BulkImportLogos onImport={handleBulkImport} data-id="element-166" />

      {/* Main Logos Management */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8" data-id="element-167">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-id="element-168">
          <div data-id="element-169">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-170">
              <Building2 className="h-5 w-5 text-blue-600" data-id="element-171" />
              Logos de Clientes
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-172">
              Gestiona los logos que aparecen en el carrusel del home. Se
              mostrarán en blanco y negro automáticamente.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-173">
            <div className="text-sm font-medium bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-100" data-id="element-174">
              {logos.length} logos
            </div>
            {logos.length > 0 && <button onClick={handleDeleteAll} className="text-sm font-medium text-red-600 hover:text-red-700 px-3 py-1 rounded-lg border border-red-200 hover:bg-red-50 transition-colors flex items-center gap-1" data-id="element-175">
                <Trash2 className="h-3 w-3" data-id="element-176" />
                Eliminar todos
              </button>}
          </div>
        </div>

        {/* Logos Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6" data-id="element-177">
          {logos.map((logo, index) => <div key={logo.id} className="group relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all" data-id="element-178">
              {/* Logo Preview */}
              <div className="aspect-video w-full relative overflow-hidden bg-white p-4 flex items-center justify-center" data-id="element-179">
                <img src={logo.url} alt={logo.name} className="max-w-full max-h-full object-contain grayscale opacity-60" data-id="element-180" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" data-id="element-181" />

                {/* Overlay controls */}
                <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 backdrop-blur-[1px]" data-id="element-182">
                  <button onClick={() => moveLogo(logo.id!, 'up')} disabled={index === 0} className="p-1.5 bg-white text-gray-700 hover:text-blue-600 rounded shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Mover arriba" data-id="element-183">
                    <ArrowUp className="h-3 w-3" data-id="element-184" />
                  </button>
                  <button onClick={() => moveLogo(logo.id!, 'down')} disabled={index === logos.length - 1} className="p-1.5 bg-white text-gray-700 hover:text-blue-600 rounded shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" title="Mover abajo" data-id="element-185">
                    <ArrowDown className="h-3 w-3" data-id="element-186" />
                  </button>
                  <button onClick={() => startEdit(logo)} className="p-1.5 bg-white text-blue-500 hover:text-blue-700 rounded shadow-sm" title="Editar" data-id="element-187">
                    <Edit2 className="h-3 w-3" data-id="element-188" />
                  </button>
                  <button onClick={() => handleDelete(logo.id!)} className="p-1.5 bg-white text-red-500 hover:text-red-700 rounded shadow-sm" title="Eliminar" data-id="element-189">
                    <Trash2 className="h-3 w-3" data-id="element-190" />
                  </button>
                </div>
              </div>

              {/* Logo Info */}
              <div className="p-2 bg-white border-t border-gray-100" data-id="element-191">
                {editingId === logo.id ? <div className="space-y-2" data-id="element-192">
                    <input type="text" value={editLogoName} onChange={e => setEditLogoName(e.target.value)} className="w-full text-xs px-2 py-1 border border-gray-300 rounded" placeholder="Nombre" data-id="element-193" />
                    <div className="flex gap-1" data-id="element-194">
                      <button onClick={() => saveEdit(logo.id!)} className="flex-1 p-1 bg-green-500 text-white rounded text-xs hover:bg-green-600" data-id="element-195">
                        <Check className="h-3 w-3 mx-auto" data-id="element-196" />
                      </button>
                      <button onClick={cancelEdit} className="flex-1 p-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400" data-id="element-197">
                        <X className="h-3 w-3 mx-auto" data-id="element-198" />
                      </button>
                    </div>
                  </div> : <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-199">
                    {logo.name}
                  </p>}
              </div>
            </div>)}
        </div>

        {/* Add New Logo Form */}
        {isAdding ? <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 space-y-3" data-id="element-200">
            <h3 className="font-semibold text-gray-900 text-sm" data-id="element-201">
              Agregar Nuevo Logo
            </h3>

            <Input label="Nombre del Cliente" value={newLogoName} onChange={e => setNewLogoName(e.target.value)} placeholder="Ej: Banco de Chile" required data-id="element-202" />

            <div data-id="element-203">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-204">
                Logo PNG con fondo transparente
              </label>
              <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800" data-id="element-205">
                ⚠️ <strong data-id="element-206">IMPORTANTE:</strong> Solo sube archivos PNG con fondo
                transparente. Los JPG tendrán fondo blanco.
              </div>
              {newLogoUrl ? <div className="relative" data-id="element-207">
                  <div className="aspect-video w-full max-w-xs bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-center" data-id="element-208">
                    <img src={newLogoUrl} alt="Preview" className="max-w-full max-h-full object-contain" data-id="element-209" />
                  </div>
                  <button onClick={() => setNewLogoUrl('')} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600" data-id="element-210">
                    <X className="h-3 w-3" data-id="element-211" />
                  </button>
                </div> : <label className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-212">
                  {isProcessing ? <Loader2 className="h-4 w-4 text-blue-500 animate-spin" data-id="element-213" /> : <Upload className="h-4 w-4 text-gray-500" data-id="element-214" />}
                  <span className="text-sm font-medium text-gray-700" data-id="element-215">
                    {isProcessing ? 'Procesando...' : 'Subir logo PNG'}
                  </span>
                  <input type="file" accept="image/png,image/svg+xml" className="hidden" onChange={handleImageUpload} disabled={isProcessing} data-id="element-216" />
                </label>}
            </div>

            <div className="flex gap-2 pt-2" data-id="element-217">
              <Button onClick={handleAddLogo} disabled={!newLogoUrl} data-id="element-218">
                Agregar Logo
              </Button>
              <Button variant="secondary" onClick={() => {
            setIsAdding(false);
            setNewLogoName('');
            setNewLogoUrl('');
          }} data-id="element-219">
                Cancelar
              </Button>
            </div>
          </div> : <button onClick={() => setIsAdding(true)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors w-full sm:w-auto" data-id="element-220">
            <Upload className="h-4 w-4 text-gray-500" data-id="element-221" />
            <span className="text-sm font-medium text-gray-700" data-id="element-222">
              Agregar logo individual
            </span>
          </button>}
      </div>
    </>;
}